import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Users, UserPlus, Scissors, TrendingUp, TrendingDown, Phone, Calendar, ChevronRight, Loader2 } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer, CartesianGrid, LineChart, Line } from "recharts";

const formatPrice = (p) => new Intl.NumberFormat("id-ID", { style: "currency", currency: "IDR", minimumFractionDigits: 0 }).format(p || 0);
const formatDateTime = (d) => d ? new Date(d).toLocaleString("id-ID", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : "-";
const formatDate = (d) => d ? new Date(d).toLocaleDateString("id-ID", { day: "2-digit", month: "short", year: "numeric" }) : "-";

const PERIOD_OPTIONS = [
  { value: "day", label: "Harian", compare: "Day-to-Day" },
  { value: "week", label: "Mingguan", compare: "Week-to-Week" },
  { value: "month", label: "Bulanan", compare: "Month-to-Month" },
  { value: "year", label: "Tahunan", compare: "Year-to-Year" },
];

function PeriodToggle({ value, onChange }) {
  return (
    <div className="flex flex-wrap gap-1 bg-[#0A0A0A] border border-[#262626] p-1" data-testid="period-toggle">
      {PERIOD_OPTIONS.map((p) => (
        <button
          key={p.value}
          onClick={() => onChange(p.value)}
          className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wider transition-colors ${value === p.value ? "bg-[#E60000] text-white" : "text-gray-500 hover:text-gray-300"}`}
          data-testid={`period-${p.value}`}
        >
          {p.label}
        </button>
      ))}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, sublabel, change, color = "text-white", testId }) {
  const positive = change !== undefined && change >= 0;
  return (
    <div className="bg-[#0F0F0F] border border-[#262626] p-5" data-testid={testId}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-xs font-bold uppercase tracking-wider text-gray-500">{label}</span>
        <Icon size={18} className={color} />
      </div>
      <p className={`font-heading text-2xl md:text-3xl font-bold ${color}`}>{value}</p>
      {sublabel && <p className="text-xs text-gray-500 mt-1">{sublabel}</p>}
      {change !== undefined && (
        <div className={`flex items-center gap-1 mt-2 text-xs font-bold ${positive ? "text-green-400" : "text-red-400"}`}>
          {positive ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
          <span>{positive ? "+" : ""}{change}% vs periode sebelumnya</span>
        </div>
      )}
    </div>
  );
}

const chartTooltipStyle = { contentStyle: { background: "#0A0A0A", border: "1px solid #262626", borderRadius: 0, color: "#fff", fontSize: 12 }, cursor: { fill: "rgba(230,0,0,0.05)" } };

// ============ TAB 1: KUNJUNGAN ============
function VisitsTab() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState("week");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("all"); // all | member | non_member

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/stats/visits?period=${period}`);
      setData(data);
    } catch { setData(null); }
    setLoading(false);
  }, [period]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 size={24} className="text-[#E60000] animate-spin" /></div>;
  if (!data) return <div className="p-6 text-gray-500 text-sm">Gagal memuat data</div>;

  const filteredVisits = filter === "all" ? data.visits : data.visits.filter(v => (filter === "member" ? v.is_member : !v.is_member));
  const compareText = PERIOD_OPTIONS.find(p => p.value === period)?.compare || "";

  return (
    <div className="space-y-5" data-testid="visits-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-xl font-bold uppercase tracking-tight text-white">Statistik Kunjungan</h2>
          <p className="text-xs text-gray-500 mt-0.5">Pembanding: {compareText}</p>
        </div>
        <PeriodToggle value={period} onChange={setPeriod} />
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <StatCard icon={Users} label="Total Kunjungan" value={data.summary.current_total} change={data.summary.change_pct} sublabel={`Sebelumnya: ${data.summary.prev_total}`} color="text-white" testId="card-visits-total" />
        <StatCard icon={Users} label="Member" value={data.summary.current_member} color="text-[#E60000]" sublabel={`${data.summary.current_total > 0 ? Math.round((data.summary.current_member / data.summary.current_total) * 100) : 0}% dari total`} testId="card-visits-member" />
        <StatCard icon={Users} label="Non-Member" value={data.summary.current_non_member} color="text-blue-400" sublabel={`${data.summary.current_total > 0 ? Math.round((data.summary.current_non_member / data.summary.current_total) * 100) : 0}% dari total`} testId="card-visits-nonmember" />
      </div>

      {/* Chart */}
      <div className="bg-[#0F0F0F] border border-[#262626] p-4" data-testid="visits-chart">
        <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">Grafik Kunjungan</p>
        <div style={{ width: "100%", height: 280 }}>
          <ResponsiveContainer>
            <BarChart data={data.chart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#262626" />
              <XAxis dataKey="label" stroke="#666" tick={{ fontSize: 11 }} />
              <YAxis stroke="#666" tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip {...chartTooltipStyle} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="member" stackId="a" fill="#E60000" name="Member" />
              <Bar dataKey="non_member" stackId="a" fill="#3B82F6" name="Non-Member" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Filter Tabs */}
      <div className="flex items-center gap-2 border-b border-[#262626]">
        {[
          { v: "all", l: `Semua (${data.visits.length})` },
          { v: "member", l: `Member (${data.visits.filter(x => x.is_member).length})` },
          { v: "non_member", l: `Non-Member (${data.visits.filter(x => !x.is_member).length})` },
        ].map(opt => (
          <button key={opt.v} onClick={() => setFilter(opt.v)} className={`px-3 py-2 text-xs font-bold uppercase tracking-wider transition-colors ${filter === opt.v ? "text-[#E60000] border-b-2 border-[#E60000]" : "text-gray-500 hover:text-gray-300"}`} data-testid={`filter-${opt.v}`}>
            {opt.l}
          </button>
        ))}
      </div>

      {/* Visits Table */}
      <div className="bg-[#0F0F0F] border border-[#262626] overflow-x-auto" data-testid="visits-table">
        <table className="w-full text-sm min-w-[640px]">
          <thead>
            <tr className="border-b border-[#262626]">
              <th className="text-left px-4 py-3 text-xs font-bold uppercase text-gray-500">Waktu</th>
              <th className="text-left px-4 py-3 text-xs font-bold uppercase text-gray-500">Pelanggan</th>
              <th className="text-left px-4 py-3 text-xs font-bold uppercase text-gray-500">Layanan</th>
              <th className="text-left px-4 py-3 text-xs font-bold uppercase text-gray-500">Tipe</th>
              <th className="text-right px-4 py-3 text-xs font-bold uppercase text-gray-500">Total</th>
              <th className="px-2 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {filteredVisits.length === 0 ? (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-gray-500 text-sm">Tidak ada kunjungan</td></tr>
            ) : filteredVisits.map((v) => (
              <tr
                key={v.transaction_id}
                onClick={() => v.customer_id && navigate(`/admin/customers?id=${v.customer_id}`)}
                className={`border-b border-[#1A1A1A] hover:bg-[#1A1A1A] transition-colors ${v.customer_id ? "cursor-pointer" : ""}`}
                data-testid={`visit-row-${v.transaction_id}`}
              >
                <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">{formatDateTime(v.created_at)}</td>
                <td className="px-4 py-3">
                  <p className="text-white font-semibold">{v.customer_name}</p>
                  {v.customer_phone && <p className="text-xs text-gray-500 flex items-center gap-1"><Phone size={10} /> {v.customer_phone}</p>}
                </td>
                <td className="px-4 py-3 text-gray-300 text-xs">{v.services.join(", ") || "-"}</td>
                <td className="px-4 py-3">
                  {v.is_member ? (
                    <span className="text-[10px] font-bold px-2 py-0.5 bg-[#E60000]/20 text-[#FF4444] border border-[#E60000]/30">MEMBER</span>
                  ) : (
                    <span className="text-[10px] font-bold px-2 py-0.5 bg-gray-700/30 text-gray-400 border border-gray-600/30">NON-MEMBER</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right text-[#E60000] font-mono font-bold whitespace-nowrap">{formatPrice(v.total_spent)}</td>
                <td className="px-2 py-3 text-gray-500">{v.customer_id && <ChevronRight size={14} />}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============ TAB 2: MEMBER BARU ============
function NewMembersTab() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState("month");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/stats/new-members?period=${period}`);
      setData(data);
    } catch { setData(null); }
    setLoading(false);
  }, [period]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 size={24} className="text-[#E60000] animate-spin" /></div>;
  if (!data) return <div className="p-6 text-gray-500 text-sm">Gagal memuat data</div>;

  const compareText = PERIOD_OPTIONS.find(p => p.value === period)?.compare || "";

  return (
    <div className="space-y-5" data-testid="new-members-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-xl font-bold uppercase tracking-tight text-white">Penginputan Member Baru</h2>
          <p className="text-xs text-gray-500 mt-0.5">Pembanding: {compareText}</p>
        </div>
        <PeriodToggle value={period} onChange={setPeriod} />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <StatCard icon={UserPlus} label="Member Baru" value={data.summary.current_total} change={data.summary.change_pct} sublabel={`Sebelumnya: ${data.summary.prev_total}`} color="text-[#E60000]" testId="card-newmembers-total" />
        <StatCard icon={Calendar} label="Rata-rata per Hari" value={(() => { const divisor = period === "day" ? 1 : period === "week" ? 7 : period === "month" ? 30 : 365; return (data.summary.current_total / divisor).toFixed(1); })()} color="text-blue-400" sublabel="Pada periode berjalan" testId="card-newmembers-avg" />
      </div>

      <div className="bg-[#0F0F0F] border border-[#262626] p-4" data-testid="new-members-chart">
        <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">Grafik Pendaftaran Member</p>
        <div style={{ width: "100%", height: 280 }}>
          <ResponsiveContainer>
            <LineChart data={data.chart}>
              <CartesianGrid strokeDasharray="3 3" stroke="#262626" />
              <XAxis dataKey="label" stroke="#666" tick={{ fontSize: 11 }} />
              <YAxis stroke="#666" tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip {...chartTooltipStyle} />
              <Line type="monotone" dataKey="count" stroke="#E60000" strokeWidth={2} dot={{ fill: "#E60000", r: 3 }} activeDot={{ r: 5 }} name="Member Baru" />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-[#0F0F0F] border border-[#262626] overflow-x-auto" data-testid="members-table">
        <table className="w-full text-sm min-w-[600px]">
          <thead>
            <tr className="border-b border-[#262626]">
              <th className="text-left px-4 py-3 text-xs font-bold uppercase text-gray-500">Tanggal Daftar</th>
              <th className="text-left px-4 py-3 text-xs font-bold uppercase text-gray-500">Nama</th>
              <th className="text-left px-4 py-3 text-xs font-bold uppercase text-gray-500">No. HP</th>
              <th className="text-left px-4 py-3 text-xs font-bold uppercase text-gray-500">Tipe</th>
              <th className="px-2 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {data.members.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-gray-500 text-sm">Belum ada member baru</td></tr>
            ) : data.members.map((m) => (
              <tr key={m.id} onClick={() => navigate(`/admin/customers?id=${m.id}`)} className="border-b border-[#1A1A1A] hover:bg-[#1A1A1A] transition-colors cursor-pointer" data-testid={`member-row-${m.id}`}>
                <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">{formatDate(m.created_at)}</td>
                <td className="px-4 py-3 text-white font-semibold">{m.name}</td>
                <td className="px-4 py-3 text-gray-300 text-xs">{m.phone || "-"}</td>
                <td className="px-4 py-3"><span className="text-[10px] font-bold px-2 py-0.5 bg-gray-700/30 text-gray-400 border border-gray-600/30 uppercase">{m.user_type}</span></td>
                <td className="px-2 py-3 text-gray-500"><ChevronRight size={14} /></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============ TAB 3: KINERJA CAPSTER ============
function CapsterTab() {
  const [period, setPeriod] = useState("month");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/stats/capsters?period=${period}`);
      setData(data);
    } catch { setData(null); }
    setLoading(false);
  }, [period]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex items-center justify-center py-20"><Loader2 size={24} className="text-[#E60000] animate-spin" /></div>;
  if (!data) return <div className="p-6 text-gray-500 text-sm">Gagal memuat data</div>;

  const chartData = data.capsters.map(c => ({ name: c.name.split(" ")[0], haircuts: c.haircuts, revenue: c.revenue }));
  const totalHaircuts = data.capsters.reduce((sum, c) => sum + c.haircuts, 0);

  return (
    <div className="space-y-5" data-testid="capster-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-heading text-xl font-bold uppercase tracking-tight text-white">Kinerja Capster</h2>
          <p className="text-xs text-gray-500 mt-0.5">Jumlah pelanggan yang dipotong per capster</p>
        </div>
        <PeriodToggle value={period} onChange={setPeriod} />
      </div>

      <div className="bg-[#0F0F0F] border border-[#262626] p-4" data-testid="capster-chart">
        <p className="text-xs font-bold uppercase tracking-wider text-gray-500 mb-3">Grafik Jumlah Potongan</p>
        <div style={{ width: "100%", height: 320 }}>
          <ResponsiveContainer>
            <BarChart data={chartData} layout="vertical" margin={{ left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#262626" />
              <XAxis type="number" stroke="#666" tick={{ fontSize: 11 }} allowDecimals={false} />
              <YAxis type="category" dataKey="name" stroke="#666" tick={{ fontSize: 11 }} width={100} />
              <Tooltip {...chartTooltipStyle} />
              <Bar dataKey="haircuts" fill="#E60000" name="Potongan" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="bg-[#0F0F0F] border border-[#262626] overflow-x-auto" data-testid="capster-table">
        <table className="w-full text-sm min-w-[640px]">
          <thead>
            <tr className="border-b border-[#262626]">
              <th className="text-left px-4 py-3 text-xs font-bold uppercase text-gray-500">#</th>
              <th className="text-left px-4 py-3 text-xs font-bold uppercase text-gray-500">Capster</th>
              <th className="text-right px-4 py-3 text-xs font-bold uppercase text-gray-500">Potongan</th>
              <th className="text-right px-4 py-3 text-xs font-bold uppercase text-gray-500">Transaksi</th>
              <th className="text-right px-4 py-3 text-xs font-bold uppercase text-gray-500">Pelanggan Unik</th>
              <th className="text-right px-4 py-3 text-xs font-bold uppercase text-gray-500">Pendapatan</th>
              <th className="text-right px-4 py-3 text-xs font-bold uppercase text-gray-500">Share</th>
            </tr>
          </thead>
          <tbody>
            {data.capsters.length === 0 ? (
              <tr><td colSpan={7} className="px-4 py-8 text-center text-gray-500 text-sm">Belum ada data capster</td></tr>
            ) : data.capsters.map((c, i) => {
              const share = totalHaircuts > 0 ? ((c.haircuts / totalHaircuts) * 100).toFixed(1) : 0;
              return (
                <tr key={c.capster_id} className="border-b border-[#1A1A1A]" data-testid={`capster-row-${c.capster_id}`}>
                  <td className="px-4 py-3 text-gray-500 font-mono">#{i + 1}</td>
                  <td className="px-4 py-3 text-white font-semibold flex items-center gap-2">
                    {c.photo ? <img src={c.photo} alt={c.name} className="w-7 h-7 rounded-full object-cover" /> : <div className="w-7 h-7 rounded-full bg-[#262626] flex items-center justify-center text-[10px] text-gray-500">{c.name.slice(0,2).toUpperCase()}</div>}
                    {c.name}
                  </td>
                  <td className="px-4 py-3 text-right text-[#E60000] font-mono font-bold">{c.haircuts}</td>
                  <td className="px-4 py-3 text-right text-white font-mono">{c.transactions}</td>
                  <td className="px-4 py-3 text-right text-white font-mono">{c.unique_customers}</td>
                  <td className="px-4 py-3 text-right text-green-400 font-mono whitespace-nowrap">{formatPrice(c.revenue)}</td>
                  <td className="px-4 py-3 text-right text-gray-400 font-mono text-xs">{share}%</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ============ MAIN PAGE ============
export default function StatisticsPage() {
  const [tab, setTab] = useState("visits");

  return (
    <div className="p-4 md:p-8" data-testid="statistics-page">
      <div className="mb-6">
        <h1 className="font-heading text-3xl font-bold uppercase tracking-tight text-white">Statistik</h1>
        <p className="text-gray-400 text-sm mt-1">Detail laporan kunjungan, member, dan kinerja capster</p>
      </div>

      {/* Main Tabs */}
      <div className="flex flex-wrap border-b border-[#262626] mb-6" data-testid="stats-tabs">
        <button onClick={() => setTab("visits")} className={`flex items-center gap-2 px-4 py-3 text-xs font-bold uppercase tracking-wider transition-colors ${tab === "visits" ? "text-[#E60000] border-b-2 border-[#E60000]" : "text-gray-500 hover:text-gray-300"}`} data-testid="tab-visits">
          <Users size={14} /> Kunjungan
        </button>
        <button onClick={() => setTab("new-members")} className={`flex items-center gap-2 px-4 py-3 text-xs font-bold uppercase tracking-wider transition-colors ${tab === "new-members" ? "text-[#E60000] border-b-2 border-[#E60000]" : "text-gray-500 hover:text-gray-300"}`} data-testid="tab-new-members">
          <UserPlus size={14} /> Member Baru
        </button>
        <button onClick={() => setTab("capster")} className={`flex items-center gap-2 px-4 py-3 text-xs font-bold uppercase tracking-wider transition-colors ${tab === "capster" ? "text-[#E60000] border-b-2 border-[#E60000]" : "text-gray-500 hover:text-gray-300"}`} data-testid="tab-capster">
          <Scissors size={14} /> Kinerja Capster
        </button>
      </div>

      {tab === "visits" && <VisitsTab />}
      {tab === "new-members" && <NewMembersTab />}
      {tab === "capster" && <CapsterTab />}
    </div>
  );
}
